"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function DifferentiationSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const comparisons = [
    {
      id: 'comp_swiping',
      traditional: 'Endless swiping through hundreds daily',
      meetx: '3 curated matches per day',
      icon: 'HandThumbUpIcon' as const,
    },
    {
      id: 'comp_matching',
      traditional: 'Based on photos and bios',
      meetx: '50+ compatibility factors analyzed',
      icon: 'SparklesIcon' as const,
    },
    {
      id: 'comp_first_date',
      traditional: 'Meet strangers immediately',
      meetx: 'Video chat before meeting',
      icon: 'VideoCameraIcon' as const,
    },
    {
      id: 'comp_verification',
      traditional: 'Anyone can join',
      meetx: 'ID + photo verification required',
      icon: 'ShieldCheckIcon' as const,
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 px-6 bg-gradient-to-b from-background via-muted/20 to-background border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className={`text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Not Another <span className="text-primary">Swiping App</span>
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            We&apos;ve reimagined dating from the ground up. Quality over quantity, always.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Traditional Apps Column */}
          <div className="space-y-4">
            <div className="text-center py-4 glass-card rounded-2xl">
              <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">
                Traditional Apps
              </h3>
            </div>
            {comparisons.map((item, index) => (
              <div
                key={`traditional_${item.id}`}
                className={`p-6 bg-muted/20 border border-white/5 rounded-2xl transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div className="flex items-start gap-4">
                  <Icon name="XMarkIcon" size={24} className="text-error mt-1" />
                  <p className="text-muted-foreground leading-relaxed">
                    {item.traditional}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* MeetX Column */}
          <div className="space-y-4">
            <div className="text-center py-4 bg-gradient-to-r from-primary to-secondary rounded-2xl">
              <h3 className="text-sm uppercase tracking-widest font-bold text-white">
                MeetX
              </h3>
            </div>
            {comparisons.map((item, index) => (
              <div
                key={`meetx_${item.id}`}
                className={`p-6 glass-card-hover rounded-2xl transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div className="flex items-start gap-4">
                  <Icon
                    name={item.icon}
                    variant="solid"
                    size={24}
                    className="text-success mt-1"
                  />
                  <p className="text-foreground font-medium leading-relaxed">
                    {item.meetx}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}